import type {
  CoreAdminProps,
  AuthProvider,
  DashboardComponent,
  LayoutComponent,
} from "ra-core";
import { CustomRoutes, localStorageStore, Resource } from "ra-core";
// GAVION: users administration screen
import { RequireAdmin } from "../../gavion/settings/users/RequireAdmin";
import { UsersSettings } from "../../gavion/routes/lazyPages"; // GAVION quality pass: lazy pages
import { SalesToUsersRedirect } from "../../gavion/settings/users/SalesToUsersRedirect";
// GAVION: layout helpers (spec 10 §4)
import { ParametresRedirect } from "../../gavion/layout/ParametresRedirect";
import { TasksPage } from "../../gavion/layout/TasksPage";
// GAVION: batch 6
import {
  DraftsPage, // GAVION ai-email
  AiSettingsPage, // GAVION ai-email
  IntegrationsSettingsPage,
  MeetingsPage,
} from "../../gavion/routes/lazyPages";
// GAVION: dialer screen /appeler (B2-T13)
import { DialerRoute } from "../../gavion/routes/lazyPages";
import {
  LeadStatusesSettingsPage,
  PipelineSettingsPage,
} from "../../gavion/routes/lazyPages"; // GAVION custom pipeline, lead statuses
// GAVION: dialer to-do list (B2-T17) and batch 2 settings screens (B2-T18)
import {
  TodoListPage,
  MyCallsPage, // GAVION my-calls
  InboundCallPage, // GAVION inbound calls
  SoftphoneSetupPage,
  CallSettingsPage,
} from "../../gavion/routes/lazyPages";
// GAVION open-pixel
import { EmailSettingsPage } from "../../gavion/routes/lazyPages";
// GAVION X1: daily goals settings
import { GoalsSettingsPage } from "../../gavion/routes/lazyPages";
// GAVION rep stats: Paramètres → Coûts (admins)
import { CostsSettingsPage } from "../../gavion/routes/lazyPages";
// GAVION commissions: Paramètres → Forfaits, Plans de commission (admins)
import {
  CommissionPlansSettingsPage,
  PackagesSettingsPage,
} from "../../gavion/routes/lazyPages";
// GAVION X3: data quality (duplicates merge, fix queue)
import {
  DataQualityPage,
  SystemHealthPage,
  PrivacySettingsPage, // GAVION: batch 7
  BookingSettingsPage, // GAVION booking
  RecruitingPage, // GAVION recruiting
} from "../../gavion/routes/lazyPages";
import {
  clearPersistedQueryCache,
  PERSISTED_QUERY_CACHE_KEY,
} from "../../gavion/offline/persistedCache"; // GAVION: offline cache cleared on logout (B7 final M11)
import { AutomationRulesPage } from "../../gavion/automation/AutomationRulesPage";
import { ObjectionsSettingsPage } from "../../gavion/objections/ObjectionsSettingsPage"; // GAVION X2
import { FormationPage } from "../../gavion/training/FormationPage"; // GAVION training
import { GavionContextPage } from "../../gavion/training/GavionContextPage"; // GAVION gavion-context
// GAVION: reports routes (spec 09 §3, §5)
import { gavionReportRoutes } from "../../gavion/reports/reportRoutes";
import { useEffect, useMemo } from "react";
import { Route } from "react-router";
// GAVION: email section routes (batch 5, spec 08)
import { emailRoutes } from "../../gavion/email/emailRoutes";
// GAVION: public unsubscribe page (batch 5, spec 08 section 7)
import { UnsubscribePage } from "../../gavion/email/unsubscribe/UnsubscribePage";
// GAVION booking: public demo booking pages (no login; hash routes, plan D1)
import {
  ManageBookingRoute,
  PublicBookingRoute,
} from "../../gavion/booking/public/routes";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { Admin } from "@/components/admin/admin";
import { ForgotPasswordPage } from "@/components/supabase/forgot-password-page";
import { SetPasswordPage } from "@/components/supabase/set-password-page";
import { ActivateAccountPage } from "@/components/gavion/auth/ActivateAccountPage";
import { OAuthConsentPage } from "@/components/supabase/oauth-consent-page";

import companies from "../companies";
import contacts from "../contacts";
// GAVION quality pass: upstream dashboards loaded on demand (fallback only)
import { Dashboard, MobileDashboard } from "../../gavion/routes/lazyPages";
import deals from "../deals";
import { Layout } from "../layout/Layout";
import { MobileLayout } from "../layout/MobileLayout";
import { SignupPage } from "../login/SignupPage";
import { ConfirmationRequired } from "../login/ConfirmationRequired";
import { ImportPage } from "../misc/ImportPage";
import { ChangelogPage } from "../misc/ChangelogPage";
import {
  getAuthProvider as defaultAuthProviderBuilder,
  getDataProvider as defaultDataProviderBuilder,
} from "../providers/supabase";
import sales from "../sales";
import { SettingsPageMobile } from "../settings/SettingsPageMobile";
import { ProfilePage } from "../settings/ProfilePage";
import { SettingsPage } from "../settings/SettingsPage";
import {
  CONFIGURATION_STORE_KEY,
  type ConfigurationContextValue,
} from "./ConfigurationContext";
import type { CrmDataProvider } from "../providers/types";
import {
  defaultCompanySectors,
  defaultCurrency,
  defaultDarkModeLogo,
  defaultDealCategories,
  defaultDealPipelineStatuses,
  defaultDealStages,
  defaultLightModeLogo,
  defaultNoteStatuses,
  defaultTaskTypes,
  defaultTitle,
} from "./defaultConfiguration";
import { i18nProvider as defaulti18nProvider } from "../providers/commons/i18nProvider";
import { StartPage } from "../login/StartPage.tsx";
import { useIsMobile } from "@/hooks/use-mobile.ts";
import { MobileTasksList } from "../tasks/MobileTasksList.tsx";
import { ContactListMobile } from "../contacts/ContactList.tsx";
import { ContactShow } from "../contacts/ContactShow.tsx";
import { CompanyShow } from "../companies/CompanyShow.tsx";
import { NoteShowPage } from "../notes/NoteShowPage.tsx";

const defaultStore = localStorageStore(undefined, "CRM");

export type CRMProps = {
  dataProvider?: CrmDataProvider;
  authProvider?: AuthProvider;
  i18nProvider?: CoreAdminProps["i18nProvider"];
  disableTelemetry?: boolean;
  store?: CoreAdminProps["store"];
  dashboard?: DashboardComponent;
  layout?: LayoutComponent;
} & Partial<ConfigurationContextValue>;

/**
 * CRM Component
 *
 * This component sets up and renders the main CRM application using `ra-core`. It provides
 * default configurations and themes but allows for customization through props. The component
 * seeds the store with any custom prop values for backwards compatibility.
 *
 * @param {LabeledValue[]} companySectors - The list of company sectors used in the application.
 * @param {string} currency - The ISO 4217 currency code used to format monetary values (e.g. "USD", "EUR", "GBP").
 * @param {RaThemeOptions} darkTheme - The theme to use when the application is in dark mode.
 * @param {LabeledValue[]} dealCategories - The categories of deals used in the application.
 * @param {string[]} dealPipelineStatuses - The statuses of deals in the pipeline used in the application.
 * @param {DealStage[]} dealStages - The stages of deals used in the application.
 * @param {RaThemeOptions} lightTheme - The theme to use when the application is in light mode.
 * @param {string} darkModeLogo - Logo shown in dark mode and on the auth pages. Must be an imported asset, an absolute URL, or a data URI — never a route-relative path like "./logos/x.svg", which breaks on nested routes such as /oauth/consent (issue #291).
 * @param {string} lightModeLogo - Logo shown in light mode. Same rule as darkModeLogo: imported asset, absolute URL, or data URI only.
 * @param {NoteStatus[]} noteStatuses - The statuses of notes used in the application.
 * @param {LabeledValue[]} taskTypes - The types of tasks used in the application.
 * @param {string} title - The title of the CRM application.
 *
 * @returns {JSX.Element} The rendered CRM application.
 *
 * @example
 * // Basic usage of the CRM component
 * import { CRM } from '@/components/atomic-crm/dashboard/CRM';
 *
 * const App = () => (
 *     <CRM
 *         darkModeLogo="https://example.com/logo-dark.svg"
 *         lightModeLogo="https://example.com/logo-light.svg"
 *         title="My Custom CRM"
 *         lightTheme={{
 *             ...defaultTheme,
 *             palette: {
 *                 primary: { main: '#0000ff' },
 *             },
 *         }}
 *     />
 * );
 *
 * export default App;
 */
export const CRM = ({
  companySectors = defaultCompanySectors,
  currency = defaultCurrency,
  dealCategories = defaultDealCategories,
  dealPipelineStatuses = defaultDealPipelineStatuses,
  dealStages = defaultDealStages,
  darkModeLogo = defaultDarkModeLogo,
  lightModeLogo = defaultLightModeLogo,
  noteStatuses = defaultNoteStatuses,
  taskTypes = defaultTaskTypes,
  title = defaultTitle,
  dataProvider = defaultDataProviderBuilder(),
  authProvider = defaultAuthProviderBuilder(),
  i18nProvider = defaulti18nProvider,
  store = defaultStore,
  disableTelemetry,
  ...rest
}: CRMProps) => {
  useEffect(() => {
    if (
      disableTelemetry ||
      process.env.NODE_ENV !== "production" ||
      typeof window === "undefined" ||
      typeof window.location === "undefined" ||
      typeof Image === "undefined"
    ) {
      return;
    }
    const img = new Image();
    img.src = `https://atomic-crm-telemetry.marmelab.com/atomic-crm-telemetry?domain=${window.location.hostname}`;
  }, [disableTelemetry]);

  // Seed the store with CRM prop values if not already stored
  // (backwards compatibility for prop-based config)
  useEffect(() => {
    if (!store.getItem(CONFIGURATION_STORE_KEY)) {
      store.setItem(CONFIGURATION_STORE_KEY, {
        companySectors,
        currency,
        dealCategories,
        dealPipelineStatuses,
        dealStages,
        noteStatuses,
        taskTypes,
        title,
        darkModeLogo,
        lightModeLogo,
      } satisfies ConfigurationContextValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  const isMobile = useIsMobile();

  // on login, pre-fetch the configuration to avoid a flickering
  // when accessing the app for the first time
  const wrappedAuthProvider = useMemo<AuthProvider>(
    () => ({
      ...authProvider,
      login: async (params: any) => {
        const result = await authProvider.login(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      handleCallback: async (params: any) => {
        if (!authProvider.handleCallback) {
          throw new Error(
            "handleCallback is not implemented in the authProvider",
          );
        }
        const result = await authProvider.handleCallback(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      logout: async (params: any) => {
        try {
          store.removeItem(CONFIGURATION_STORE_KEY);
        } catch {
          // Ignore
        }
        clearPersistedQueryCache(); // GAVION: no lead data left on the device (B7 final M11)
        return authProvider.logout(params);
      },
    }),
    [authProvider, dataProvider, store],
  );

  const ResponsiveAdmin = isMobile ? MobileAdmin : DesktopAdmin;

  return (
    <ResponsiveAdmin
      dataProvider={dataProvider}
      authProvider={wrappedAuthProvider}
      i18nProvider={i18nProvider}
      store={store}
      loginPage={StartPage}
      requireAuth
      disableTelemetry
      {...rest}
    />
  );
};

const DesktopAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
  },
) => {
  return (
    <Admin
      layout={props.layout ?? Layout}
      dashboard={props.dashboard ?? Dashboard}
      {...props}
    >
      <CustomRoutes noLayout>
        <Route path={SignupPage.path} element={<SignupPage />} />
        {/* GAVION: public unsubscribe page (no login) */}
        <Route path="/desabonnement" element={<UnsubscribePage />} />
        {/* GAVION booking: public demo booking and manage pages (no login) */}
        <Route path="/demo/gerer/:token" element={<ManageBookingRoute />} />
        <Route path="/demo/:slug" element={<PublicBookingRoute />} />
        <Route
          path={ConfirmationRequired.path}
          element={<ConfirmationRequired />}
        />
        <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
        {/* GAVION: scanner-proof invite / recovery landing page (no login) */}
        <Route
          path={ActivateAccountPage.path}
          element={<ActivateAccountPage />}
        />
        <Route
          path={ForgotPasswordPage.path}
          element={<ForgotPasswordPage />}
        />
        <Route path={OAuthConsentPage.path} element={<OAuthConsentPage />} />
      </CustomRoutes>
      <CustomRoutes>
        <Route path={ProfilePage.path} element={<ProfilePage />} />
        <Route path={SettingsPage.path} element={<SettingsPage />} />
        <Route path={ImportPage.path} element={<ImportPage />} />
        <Route path={ChangelogPage.path} element={<ChangelogPage />} />
        {/* GAVION: dialer screen (B2-T13) */}
        <Route path="/appeler" element={<DialerRoute />} />
        {/* GAVION: dialer to-do list (B2-T17) */}
        <Route path="/appeler/a-completer" element={<TodoListPage />} />
        {/* GAVION my-calls: « Mes appels » (call history) */}
        <Route path="/appeler/historique" element={<MyCallsPage />} />
        {/* GAVION inbound calls: « Appel entrant » (opened by MicroSIP) */}
        <Route path="/appel-entrant" element={<InboundCallPage />} />
        {/* GAVION: batch 2 settings (B2-T18); automations stay open to reps (read-only view) */}
        <Route path="/parametres/telephone" element={<SoftphoneSetupPage />} />
        {/* GAVION ai-email: proposed drafts */}
        <Route path="/brouillons" element={<DraftsPage />} />
        {/* GAVION ai-email: Paramètres → IA (admin parts gate themselves) */}
        <Route path="/parametres/ia" element={<AiSettingsPage />} />
        <Route
          path="/parametres/automatisations"
          element={<AutomationRulesPage />}
        />
        {/* GAVION X2: objection library (reps read-only, admins edit) */}
        <Route
          path="/parametres/objections"
          element={<ObjectionsSettingsPage />}
        />
        {/* GAVION custom pipeline: Paramètres → Pipeline (admins) */}
        <Route
          path="/parametres/pipeline"
          element={
            <RequireAdmin>
              <PipelineSettingsPage />
            </RequireAdmin>
          }
        />
        {/* GAVION lead statuses: Paramètres → Statuts des leads (admins) */}
        <Route
          path="/parametres/statuts-leads"
          element={
            <RequireAdmin>
              <LeadStatusesSettingsPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/parametres/appels"
          element={
            <RequireAdmin>
              <CallSettingsPage />
            </RequireAdmin>
          }
        />
        {/* GAVION open-pixel: Paramètres → Courriels */}
        <Route
          path="/parametres/courriels"
          element={
            <RequireAdmin>
              <EmailSettingsPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/parametres/objectifs"
          element={
            <RequireAdmin>
              <GoalsSettingsPage />
            </RequireAdmin>
          }
        />
        {/* GAVION rep stats: Paramètres → Coûts */}
        <Route
          path="/parametres/couts"
          element={
            <RequireAdmin>
              <CostsSettingsPage />
            </RequireAdmin>
          }
        />
        {/* GAVION commissions: Paramètres → Forfaits, Plans de commission (admins) */}
        <Route
          path="/parametres/forfaits"
          element={
            <RequireAdmin>
              <PackagesSettingsPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/parametres/plans-commission"
          element={
            <RequireAdmin>
              <CommissionPlansSettingsPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/parametres/qualite"
          element={
            <RequireAdmin>
              <DataQualityPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/parametres/sante"
          element={
            <RequireAdmin>
              <SystemHealthPage />
            </RequireAdmin>
          }
        />
        {/* GAVION: batch 7: Confidentialité (spec 11 §1, admins only) */}
        <Route
          path="/parametres/confidentialite"
          element={
            <RequireAdmin>
              <PrivacySettingsPage />
            </RequireAdmin>
          }
        />
        {/* GAVION recruiting: « Recrutement » (admins only) */}
        {[
          "/recrutement",
          "/recrutement/candidats",
          "/recrutement/candidats/:uid",
        ].map((path) => (
          <Route
            key={path}
            path={path}
            element={
              <RequireAdmin>
                <RecruitingPage />
              </RequireAdmin>
            }
          />
        ))}
        {/* GAVION: reports (spec 09 §3, §5) */}
        {gavionReportRoutes()}
        {/* GAVION batch 3: integrations page (open to all; Teams card gates itself) */}
        <Route
          path="/parametres/integrations"
          element={<IntegrationsSettingsPage />}
        />
        {/* GAVION booking: every user's own demo booking link (the page gates the admin parts) */}
        <Route
          path="/parametres/calendrier-demo"
          element={<BookingSettingsPage />}
        />
        {/* GAVION: batch 6 meetings inbox */}
        <Route path="/reunions" element={<MeetingsPage />} />
        {/* GAVION training: gamified dialer course (every user; admins also see « Équipe ») */}
        <Route path="/formation" element={<FormationPage />} />
        {/* GAVION gavion-context: « Tout sur Gavion » (every user, read only) */}
        <Route path="/formation/gavion" element={<GavionContextPage />} />
        {/* GAVION: /parametres index redirect */}
        <Route path="/parametres" element={<ParametresRedirect />} />
        {/* GAVION: users administration (admins only) */}
        <Route
          path="/parametres/utilisateurs"
          element={
            <RequireAdmin>
              <UsersSettings />
            </RequireAdmin>
          }
        />
        {/* GAVION: invite-only. These static routes outrank the sales Resource's /sales/* routes, which stay registered for the data layer. */}
        <Route path="/sales" element={<SalesToUsersRedirect />} />
        <Route path="/sales/create" element={<SalesToUsersRedirect />} />
        <Route path="/sales/:id" element={<SalesToUsersRedirect />} />
        {/* GAVION: email section (batch 5); admin-only children are wrapped in RequireAdmin */}
        {emailRoutes}
      </CustomRoutes>
      <Resource name="deals" {...deals} />
      <Resource name="contacts" {...contacts} />
      <Resource name="companies" {...companies} />
      <Resource name="contact_notes" />
      <Resource name="deal_notes" />
      <Resource name="tasks" list={TasksPage} />{" "}
      {/* GAVION: desktop tasks list */}
      <Resource name="sales" {...sales} />
      <Resource name="tags" />
    </Admin>
  );
};

const MobileAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
  },
) => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: 1000 * 60 * 60 * 24, // 24 hours
        networkMode: "offlineFirst",
      },
      mutations: {
        networkMode: "offlineFirst",
      },
    },
  });
  const asyncStoragePersister = createAsyncStoragePersister({
    storage: localStorage,
    key: PERSISTED_QUERY_CACHE_KEY, // GAVION: the key logout clears (B7 final M11)
  });

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: asyncStoragePersister,
        dehydrateOptions: {
          // Picks the queries saved to localStorage for offline use: successful
          // ones (react-query's default rule, inlined because the persister
          // types its own copy of query-core), except those flagged with
          // `meta: { persist: false }`, like the global search, which caches one
          // query per typed term.
          shouldDehydrateQuery: (query) =>
            query.state.status === "success" && query.meta?.persist !== false,
        },
      }}
    >
      <Admin
        queryClient={queryClient}
        layout={props.layout ?? MobileLayout}
        dashboard={props.dashboard ?? MobileDashboard}
        {...props}
      >
        <CustomRoutes noLayout>
          <Route path={SignupPage.path} element={<SignupPage />} />
          {/* GAVION: public unsubscribe page (no login) */}
          <Route path="/desabonnement" element={<UnsubscribePage />} />
          {/* GAVION booking: public demo booking and manage pages (no login) */}
          <Route path="/demo/gerer/:token" element={<ManageBookingRoute />} />
          <Route path="/demo/:slug" element={<PublicBookingRoute />} />
          <Route
            path={ConfirmationRequired.path}
            element={<ConfirmationRequired />}
          />
          <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
          {/* GAVION: scanner-proof invite / recovery landing page (no login) */}
          <Route
            path={ActivateAccountPage.path}
            element={<ActivateAccountPage />}
          />
          <Route
            path={ForgotPasswordPage.path}
            element={<ForgotPasswordPage />}
          />
          <Route path={OAuthConsentPage.path} element={<OAuthConsentPage />} />
        </CustomRoutes>
        <CustomRoutes>
          {/* GAVION: dialer screen (B2-T13) */}
          <Route path="/appeler" element={<DialerRoute />} />
          {/* GAVION: dialer to-do list (B2-T17) */}
          <Route path="/appeler/a-completer" element={<TodoListPage />} />
          {/* GAVION my-calls: « Mes appels » (call history) */}
          <Route path="/appeler/historique" element={<MyCallsPage />} />
          {/* GAVION inbound calls: « Appel entrant » (opened by MicroSIP) */}
          <Route path="/appel-entrant" element={<InboundCallPage />} />
          {/* GAVION: batch 2 settings (B2-T18); automations stay open to reps (read-only view) */}
          <Route
            path="/parametres/telephone"
            element={<SoftphoneSetupPage />}
          />
          {/* GAVION ai-email: proposed drafts */}
          <Route path="/brouillons" element={<DraftsPage />} />
          {/* GAVION ai-email: Paramètres → IA (admin parts gate themselves) */}
          <Route path="/parametres/ia" element={<AiSettingsPage />} />
          <Route
            path="/parametres/automatisations"
            element={<AutomationRulesPage />}
          />
          {/* GAVION X2: objection library (reps read-only, admins edit) */}
          <Route
            path="/parametres/objections"
            element={<ObjectionsSettingsPage />}
          />
          {/* GAVION custom pipeline: Paramètres → Pipeline (admins) */}
          <Route
            path="/parametres/pipeline"
            element={
              <RequireAdmin>
                <PipelineSettingsPage />
              </RequireAdmin>
            }
          />
          {/* GAVION lead statuses: Paramètres → Statuts des leads (admins) */}
          <Route
            path="/parametres/statuts-leads"
            element={
              <RequireAdmin>
                <LeadStatusesSettingsPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/parametres/appels"
            element={
              <RequireAdmin>
                <CallSettingsPage />
              </RequireAdmin>
            }
          />
          {/* GAVION open-pixel: Paramètres → Courriels */}
          <Route
            path="/parametres/courriels"
            element={
              <RequireAdmin>
                <EmailSettingsPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/parametres/objectifs"
            element={
              <RequireAdmin>
                <GoalsSettingsPage />
              </RequireAdmin>
            }
          />
          {/* GAVION rep stats: Paramètres → Coûts */}
          <Route
            path="/parametres/couts"
            element={
              <RequireAdmin>
                <CostsSettingsPage />
              </RequireAdmin>
            }
          />
          {/* GAVION commissions: Paramètres → Forfaits, Plans de commission (admins) */}
          <Route
            path="/parametres/forfaits"
            element={
              <RequireAdmin>
                <PackagesSettingsPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/parametres/plans-commission"
            element={
              <RequireAdmin>
                <CommissionPlansSettingsPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/parametres/qualite"
            element={
              <RequireAdmin>
                <DataQualityPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/parametres/sante"
            element={
              <RequireAdmin>
                <SystemHealthPage />
              </RequireAdmin>
            }
          />
          {/* GAVION: batch 7: Confidentialité (spec 11 §1, admins only) */}
          <Route
            path="/parametres/confidentialite"
            element={
              <RequireAdmin>
                <PrivacySettingsPage />
              </RequireAdmin>
            }
          />
          {/* GAVION recruiting: « Recrutement » (admins only) */}
          {[
            "/recrutement",
            "/recrutement/candidats",
            "/recrutement/candidats/:uid",
          ].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <RequireAdmin>
                  <RecruitingPage />
                </RequireAdmin>
              }
            />
          ))}
          {/* GAVION: reports (spec 09 §3, §5) */}
          {gavionReportRoutes()}
          {/* GAVION batch 3: integrations page (open to all; Teams card gates itself) */}
          <Route
            path="/parametres/integrations"
            element={<IntegrationsSettingsPage />}
          />
          {/* GAVION booking: every user's own demo booking link (the page gates the admin parts) */}
          <Route
            path="/parametres/calendrier-demo"
            element={<BookingSettingsPage />}
          />
          {/* GAVION: batch 6 meetings inbox */}
          <Route path="/reunions" element={<MeetingsPage />} />
          {/* GAVION training */}
          <Route path="/formation" element={<FormationPage />} />
          <Route path="/formation/gavion" element={<GavionContextPage />} />
          <Route path="/parametres" element={<ParametresRedirect />} />
          {/* GAVION: users administration (admins only); /parametres redirects admins here on mobile too */}
          <Route
            path="/parametres/utilisateurs"
            element={
              <RequireAdmin>
                <UsersSettings />
              </RequireAdmin>
            }
          />
          <Route path={ProfilePage.path} element={<ProfilePage />} />
          <Route
            path={SettingsPageMobile.path}
            element={<SettingsPageMobile />}
          />
          <Route path={ChangelogPage.path} element={<ChangelogPage />} />
          {/* GAVION: email section (batch 5); admin-only children are wrapped in RequireAdmin */}
          {emailRoutes}
        </CustomRoutes>
        <Resource
          name="contacts"
          list={ContactListMobile}
          show={ContactShow}
          recordRepresentation={contacts.recordRepresentation}
        >
          <Route path=":id/notes/:noteId" element={<NoteShowPage />} />
        </Resource>
        {/* GAVION: tab targets; desktop list/deals board are responsive */}
        <Resource name="companies" list={companies.list} show={CompanyShow} />
        <Resource name="deals" list={deals.list} />
        <Resource name="tasks" list={MobileTasksList} />
      </Admin>
    </PersistQueryClientProvider>
  );
};
